import { api } from '@/shared/utils/api/api';

// --- Session Management ---
const SESSION_KEY = 'analytics_session_id';
const SESSION_DURATION_MS = 30 * 60 * 1000; // 30 minutes

function getSessionId(): string {
  try {
    const stored = sessionStorage.getItem(SESSION_KEY);
    if (stored) {
      const { id, expiresAt } = JSON.parse(stored);
      if (Date.now() < expiresAt) {
        // Extend session on activity
        sessionStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ id, expiresAt: Date.now() + SESSION_DURATION_MS })
        );
        return id;
      }
    }
  } catch {
    // Corrupted storage, generate new
  }

  const id = crypto.randomUUID();
  sessionStorage.setItem(
    SESSION_KEY,
    JSON.stringify({ id, expiresAt: Date.now() + SESSION_DURATION_MS })
  );
  return id;
}

// --- Event Types ---
interface BaseEvent {
  event_type: string;
  session_id: string;
  timestamp: string;
  properties?: Record<string, unknown>;
}

interface PageViewEvent extends BaseEvent {
  event_type: 'page_view';
  entity_type?: string;
  entity_id?: number;
}

interface SearchEvent extends BaseEvent {
  event_type: 'search';
  search_query: string;
  result_count: number;
}

interface AffiliateClickEvent extends BaseEvent {
  event_type: 'affiliate_click';
  entity_type: 'brand';
  entity_id: number;
  properties: {
    affiliate_program_id?: number;
    destination_url?: string;
  };
}

type AnalyticsEvent = PageViewEvent | SearchEvent | AffiliateClickEvent;

// --- Batching Queue ---
const eventQueue: AnalyticsEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_INTERVAL_MS = 3000; // flush every 3s
const MAX_BATCH_SIZE = 10;

async function flush(): Promise<void> {
  if (eventQueue.length === 0) return;

  const batch = eventQueue.splice(0, MAX_BATCH_SIZE);

  try {
    await api({
      endpoint: '/v1/public/track',
      options: {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ events: batch }),
      },
    });
  } catch (err) {
    console.warn('[Analytics] Failed to send events:', err);
    // Re-queue failed events at the front (with a cap to prevent infinite growth)
    if (eventQueue.length < 100) {
      eventQueue.unshift(...batch);
    }
  }
}

function scheduleFlush(): void {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

function enqueue(event: AnalyticsEvent): void {
  eventQueue.push(event);

  if (eventQueue.length >= MAX_BATCH_SIZE) {
    flush();
  } else {
    scheduleFlush();
  }
}

// Flush remaining events when the user leaves
if (typeof window !== 'undefined') {
  window.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flush();
    }
  });
}

// --- Public API ---

export function trackPageView(path: string, entity?: { type: string; id: number }): void {
  enqueue({
    event_type: 'page_view',
    session_id: getSessionId(),
    timestamp: new Date().toISOString(),
    entity_type: entity?.type,
    entity_id: entity?.id,
    properties: { path },
  });
}

export function trackSearch(query: string, resultCount: number): void {
  enqueue({
    event_type: 'search',
    session_id: getSessionId(),
    timestamp: new Date().toISOString(),
    search_query: query,
    result_count: resultCount,
  });
}

export function trackAffiliateClick(
  brandId: number,
  destinationUrl: string,
  affiliateProgramId?: number
): void {
  enqueue({
    event_type: 'affiliate_click',
    session_id: getSessionId(),
    timestamp: new Date().toISOString(),
    entity_type: 'brand',
    entity_id: brandId,
    properties: {
      affiliate_program_id: affiliateProgramId,
      destination_url: destinationUrl,
    },
  });
}

export function trackEntityView(entityType: string, entityId: number, path: string): void {
  enqueue({
    event_type: 'page_view',
    session_id: getSessionId(),
    timestamp: new Date().toISOString(),
    entity_type: entityType,
    entity_id: entityId,
    properties: { path },
  });
}
